// The student's adaptive-practice session: loads a skill through the adaptive engine and walks
// the child question by question, advancing their level, awarding coins and streaks, and
// interrupting for the Safe Zone and skill-mastered popups. Launched in an iframe from
// student-web. The teacher's view of the same skill is adaptivePracticeForTeachers.
//
// Named for the ROLE, not a grade band: adaptivePractice.tsx picks between this and the teacher
// screen on currentUser.role alone - grade has never been part of that choice, so a widening
// grade range (1-5, then 1-8, then 9-10) must never require renaming this file again.

import { AdaptivePracticeContext, AppContext } from "../../context/context";
import { PlayFrame } from "./frame/PlayFrame";
import { ContentFactoryImpl, Content } from '@mathbuddy/adaptive-practice-engine';
import { getTimeSpent } from '../../utils/utils';
import { Component } from "react";
import { StatusCodes } from "http-status-codes";
import apis from "../../apis/api";
import { ls } from "../../localStorage";
import { adaptivePracticeActions } from "../../reduxStore";
import { connect } from "react-redux";
import { applyTheme, resolveTheme } from './theme';



class AdaptivePracticeForStudents extends Component<any, any> {
    static contextType = AppContext;
    context!: { windowHeight: string, windowWidth: string, messageForParent: any, navigateTo: any };
    constructor(props: any) {
        super(props)
        this.state = {
            // Optional `theme` prop from the parent; falls back to this screen's own
            // default. Resolved once - the theme cannot change between questions.
            theme: resolveTheme(props.theme),
            currentUser: this.props.currentUser,
            skillId: this.props.skillId,
            langObj: { number_lang: '', lang: '' },
            contentFactoryObj: new ContentFactoryImpl(),
            contentObj: new Content(),
            isPractice: false,
            isNewSession: true,
            isLoader: false,
            isCoinAnimation: false,
            practiceCount: 0,
            coins: 0,
            streaks: 0,
            adaptivePracticeLevel: '',
            completedStatusPercentage: 0,
            currentQuestionPosition: 0,
            startTime: 0,
            totalCorrectAttemptedQuestion: 0,
            totalAttemptedQuestions: 0,
            totalAttemptedCount: 0,
            accuracy: 0,
            notificationMessage: '',
            response: { component: [] },
            questionSkill: {},
            lastResponses: { questions: [] },
            isShowAdaptivePopup: false,
            isShowStartPopup: false,
            isCloseFromClick: false,
            isSafeZone: false,
            isLastQuestion: false,
        }
    }

    componentDidMount = () => {
        // Paint the frame ONCE per screen - 21 custom properties, not a per-question cost.
        applyTheme(this.state.theme);
        this.getLatestStateOnMount();
    }


    getLatestStateOnMount = async () => {
        await this.sendLastAttemptedIfAny(); // if window is reloaded and last attempted questions is there send it to DB.
        await this.addSmartXP(); // if window is reloaded and smart XP not added.
        this.setState({ isLoader: true });
        let skillStatus = await this.getSkillStatus();
        let streaksAndCoins = await this.getStreaksAndCoins();
        let currentUser = this.props.currentUser;
        let country_code = currentUser.isB2C ? currentUser.country_code : currentUser.classroom_country_code;

        let langObj = { number_lang: 'en', lang: this.props.lang + '_' + country_code.toLowerCase() };
        this.setState({ ...skillStatus, ...streaksAndCoins, langObj, isLoader: false }, () => {
            // console.log(this.props);
            // console.log(this.state);
            if (!this.state.isShowStartPopup)
                this.loadSkillContent();
        })
    }

    getSkillStatus = async () => {
        let returnObj = {
            totalAttemptedCount: 0,
            totalCorrectAttemptedQuestion: 0,
            isPractice: false,
            practiceCount: 0,
            isShowStartPopup: false,
        }

        let skillStatusData = {
            "filterList": [
                {
                    "columnName": "user_id",
                    "type": "filterId",
                    "value": [this.state.currentUser.id]
                },
                {
                    "columnName": "lo_id",
                    "type": "filterId",
                    "value": [this.state.skillId]
                }
            ],
            "sortHeader": "createdAt",
            "sortDirection": "DESC",
            "page": 1,
            "limit": 1,
            "isPagination": true
        }

        if (this.state.currentUser.classroom_id) {
            skillStatusData.filterList.push({
                "columnName": "classroom_id",
                "type": "filterId",
                "value": [this.state.currentUser.classroom_id]
            });
        }

        let skillStatusRes = await apis._getSkillStatus(skillStatusData);
        //console.log(skillStatusRes);
        if (skillStatusRes?.data?.code === StatusCodes.OK) {
            if (skillStatusRes.data.result.data.length > 0) {
                returnObj.totalAttemptedCount = skillStatusRes.data.result.data[0].questions
                returnObj.totalCorrectAttemptedQuestion = skillStatusRes.data.result.data[0].correct_answers
                returnObj.isPractice = skillStatusRes.data.result.data[0].current_level === "Mastered" ? true : false;
                returnObj.isShowStartPopup = returnObj.isPractice;
            }
        }

        if (returnObj.isPractice) {
            let todaysSmartXPSummary = await apis._getTodaysSmartXPSummary({
                user_id: this.state.currentUser.id,
                classroom_id: this.state.currentUser.classroom_id,
                lo_id: this.state.skillId
            });
            // console.log("todaysSmartXPSummary", todaysSmartXPSummary);
            if (todaysSmartXPSummary.status === StatusCodes.OK && todaysSmartXPSummary.data.data.practice_sessions_count) {
                returnObj.practiceCount = todaysSmartXPSummary.data.data.practice_sessions_count;
            }
        }

        return returnObj;
    }

    getStreaksAndCoins = async () => {
        let returnObj = { coins: 0, streaks: 0 };

        let currentUser = this.state.currentUser;
        let dashboardSummary = await apis._getLastUserSummary(currentUser.id);
        if (dashboardSummary.status === StatusCodes.OK && dashboardSummary.data.result.length > 0) {
            let lastSummary = dashboardSummary.data.result[0];

            returnObj.coins = lastSummary.total_coins
            returnObj.streaks = lastSummary.current_streak;
        }

        return returnObj;
    }

    loadSkillContent = async () => {
        this.state.contentObj.setTheme(this.state.theme);
        try {
            let isPractice = this.state.isPractice;
            this.setState({ isLoader: true, response: { component: [] } })

            let currentUser = this.state.currentUser;
            let skillId = this.state.skillId;
            let userObj = {
                env: process.env.REACT_APP_ENV, userId: currentUser.id,
                classroomId: currentUser.classroom_id,
                dbToken: currentUser.dbToken, token: ls.getToken()
            };
            let currentQuestionPosition = (isPractice) ? this.state.currentQuestionPosition : 0;
            let langObj = this.state.langObj;
            // console.log(langObj);
            let passingResponses = (this.state.lastResponses.questions.length < 2) ? [] : this.state.lastResponses.questions.slice(-2);
            //  console.log("slicedArray",passingResponses)
            let questionSkill = (currentQuestionPosition === 0) ? await this.state.contentObj.loadSkill(skillId, userObj, langObj.lang, this.state.adaptivePracticeLevel, passingResponses) :
                this.state.questionSkill;

            let currentQuestion: { questionId: string, safeZone: boolean } = questionSkill.questions[currentQuestionPosition];

            let isLastQuestion = (!isPractice) ?
                questionSkill.nextLevel === "Mastered" :
                (currentQuestionPosition === (questionSkill.questions.length - 1));

            // console.log("question_skill", questionSkill, "currentlevel", questionSkill.currentLevel, "adaptivelevel", this.state.adaptivePracticeLevel, passingResponses);
            // If safeZone question comes first time show start safeZone popUp;
            if (currentQuestion.safeZone && !this.state.isSafeZone) {
                this.setState({ isLoader: false, isShowStartPopup: !isPractice, isSafeZone: true })
                if (!isPractice)
                    return;
            }

            let questionId = currentQuestion.questionId;
            let adaptivePracticeLevel = (isPractice) ? 'Question ' + (this.state.currentQuestionPosition + 1)
                : questionSkill.currentLevel;
            let completedStatusPercentage = (isPractice) ? Math.round(((currentQuestionPosition) * 100) / questionSkill.questions.length) :
                Math.round(((questionSkill.meter.current - 1) * 100) / questionSkill.meter.total);
            let accuracy = Math.round((this.state.totalCorrectAttemptedQuestion * 100) / (this.state.totalAttemptedCount)) || 0;
            let totalAttemptedQuestions = (!isPractice) ? questionSkill.meter.current - 1 : this.state.totalAttemptedQuestions;


            let response = await this.state.contentFactoryObj.displayQuestion(this.state.contentObj,
                this.state.contentObj.createContentTemplateObject(questionId, 'REACT_WEB'), langObj);
            console.log("response_skills", response, this.state.lastResponses);
            this.setState({
                startTime: new Date(), currentQuestionPosition, adaptivePracticeLevel, questionSkill,
                completedStatusPercentage, response, accuracy, totalAttemptedQuestions,
                isSafeZone: currentQuestion.safeZone, isLastQuestion
            }, () => {
                //console.log(this.state);
                this.setState({ isLoader: false });
            })

        } catch (error) {
            this.setState({ isLoader: false })
        }
    }

    getNotificationMessage = (streak: number) => {
        let message = ''
        if (streak > 0 && (streak % 2) === 0) {
            message = (streak < 10) ? `${streak} in a row!` :
                (streak < 20) ? `You are on a roll! ${streak} in a row` :
                    (streak <= 22) ? `Bravo! ${streak} in a row` :
                        `Way to go. ${streak} in a row`;
        }
        return message;
    }

    saveQuestionResponse = async (answerObj: { givenAnswer: any, actualAnswer: any, isCorrectAns: Boolean }, clickCount: number) => {
        let QS = this.state.questionSkill;
        let isPractice = this.state.isPractice;
        let isCorrectAns = answerObj.isCorrectAns;
        let isSafeZone = this.state.isSafeZone;
        let isLastQuestion = this.state.isLastQuestion;

        let coins = (isCorrectAns) ? (this.state.coins + 1) : this.state.coins;
        let streaks = (isCorrectAns) ? (this.state.streaks + 1) : isSafeZone ? this.state.streaks : 0;
        let time_spent = getTimeSpent(this.state.startTime);
        let currentQuestionPosition = (isPractice && (isSafeZone || isCorrectAns || clickCount > 1)) ? (this.state.currentQuestionPosition + 1) :
            (isPractice && !isCorrectAns && clickCount == 1) ? (this.state.currentQuestionPosition) : 0; //before mastry engine will give only one question at a time so it's 0
        let adaptivePracticeLevel = (!isPractice && ((isCorrectAns && clickCount == 1) || isSafeZone)) ? QS.nextLevel : this.state.adaptivePracticeLevel;
        let completedStatusPercentage = (isPractice) ? Math.round(((currentQuestionPosition) * 100) / QS.questions.length) :
            ((isCorrectAns && clickCount == 1) || isSafeZone) ? Math.round(((QS.meter.current) * 100) / QS.meter.total) :
                this.state.completedStatusPercentage;
        let notificationMessage = isCorrectAns ? this.getNotificationMessage(streaks) : '';
        let isMastered = (!isPractice && ((isCorrectAns && clickCount == 1) || isSafeZone) && QS.nextLevel === "Mastered") ? true : false;
        let isShowAdaptivePopup =
            (isLastQuestion && isSafeZone && !isCorrectAns) ? false :
                isMastered ? true :
                    (isPractice && currentQuestionPosition === QS.questions.length) ? true : false;
        let totalCorrectAttemptedQuestion = (isCorrectAns) ? this.state.totalCorrectAttemptedQuestion + 1 : this.state.totalCorrectAttemptedQuestion;

        let totalAttemptedQuestions = (!isPractice && ((isCorrectAns && clickCount === 1) || isSafeZone)) ? QS.meter.current
            : (isPractice && (isSafeZone || isCorrectAns || clickCount > 1)) ? this.state.totalAttemptedQuestions + 1
                : this.state.totalAttemptedQuestions;

        let totalAttemptedCount = this.state.totalAttemptedCount + 1;
        let accuracy = Math.round((totalCorrectAttemptedQuestion * 100) / totalAttemptedCount) || 0;

        this.setState({
            isCoinAnimation: isCorrectAns, coins, streaks, completedStatusPercentage, currentQuestionPosition,
            adaptivePracticeLevel, notificationMessage, totalCorrectAttemptedQuestion, totalAttemptedQuestions, accuracy, totalAttemptedCount,
            isShowStartPopup: false, isCloseFromClick: false
        },
            () => {
                setTimeout(() => {
                    this.setState({ isCoinAnimation: false, notificationMessage: '', isShowAdaptivePopup })
                    /* if (isShowAdaptivePopup)
                       this.playMusic('successSound');*/
                }, 1700)
            });

        //Here we are maintaining lastattempted to redux for if(!isSafeZone && clickCount === 1 && !answerObj.isCorrectAns)
        // we are not storing responce to db if incorrect in first attempt and maintain in redux 
        let lastAttemptedData = this.props.adaptivePracticeReducer.lastAttemptedQuestion.questions[0];

        //Just verify if last atempted is belongs to current attemp if any glitch or mismatch.
        if (!(lastAttemptedData.user_id &&
            lastAttemptedData.user_id.toString() === this.state.currentUser.id.toString() &&
            lastAttemptedData.lo_id.toString() === this.state.skillId.toString() &&
            lastAttemptedData.question_id.toString() ===
            QS.questions[this.state.currentQuestionPosition].questionId.toString())
        ) {
            this.props.setAPDefaultLastAttempted();// if Last Attempted don't match with current set to default
            lastAttemptedData = { answers: [], time_spent: 0 };
        }

        let currentAnswer = [{ "isCorrect": answerObj.isCorrectAns, "answer": answerObj.givenAnswer }];

        let data = {
            "questions": [{
                "isNewSession": this.state.isNewSession,
                "isReAttempt": false,
                "user_id": this.state.currentUser.id,
                "lo_id": this.state.skillId,
                "question_id": QS.questions[this.state.currentQuestionPosition].questionId,
                "review_obj": this.state.response.reviewObj,
                "number_lang": this.state.langObj.number_lang,
                "lang": this.state.langObj.lang,
                "answers": [...lastAttemptedData.answers, ...currentAnswer],
                "isCorrect": answerObj.isCorrectAns,
                "isSafeZone": isSafeZone,
                "isChallenge": false,
                "isChallengeCompleted": false,
                "blooms": this.state.response.blooms,
                "learningGaps": this.state.response.learningGaps || [],
                "time_spent": lastAttemptedData.time_spent + time_spent,
                "current_level": (isSafeZone || (answerObj.isCorrectAns === true && clickCount === 1)) ? QS.nextLevel : QS.currentLevel,
                "classroom_id": this.state.currentUser.classroom_id,
            }]
        }

        // Maintain correct count session wise to be sent at the end of session for SMART XP.
        // This Will reset at the end of each session.
        if (isCorrectAns) {
            let correctCount = this.props.adaptivePracticeReducer.smartXpSession?.correct_count;
            if (correctCount === undefined || correctCount === null)
                correctCount = 0;

            correctCount++;
            let smartXpSession = {
                user_id: this.state.currentUser.id,
                classroom_id: this.state.currentUser.classroom_id,
                lo_id: this.state.skillId,
                correct_count: correctCount,
                isPractice
            };

            this.props.setAPSmartXpSession(smartXpSession);
        }

        if (!isSafeZone && clickCount === 1 && !answerObj.isCorrectAns) {
            this.props.setAPLastAttempted(data);
            return  // return from here if answer is incorrect in the first attempt and not a safe zone question.
        }

        let lastResponses = this.state.lastResponses;
        lastResponses.questions.push(data.questions[0])
        // console.log(data);

        this.setState({ lastResponses, isNewSession: false },
            async () => {
                let response = await this.saveQuestionResponseToDB(data, isMastered, isPractice);
                // console.log(response);
                if (response?.status === StatusCodes.OK) {
                    if (isCorrectAns && !isShowAdaptivePopup)
                        this.loadSkillContent();

                    if (isShowAdaptivePopup)
                        this.addSmartXP();
                }
            });
    }

    saveQuestionResponseToDB = async (data: any, isMastered = false, isPractice = false) => {
        // console.log(data);
        let response = await apis._saveQuestionResponse(data);
        this.props.setAPDefaultLastAttempted();// Always set default last attempted question with empty answer array.
        if (response?.status === StatusCodes.OK) {
            this.context.messageForParent('updateUserReducerForAdaptivePractice', {
                questions: data.questions, //This becouse we are storing one question at a time.
                isMastered: isMastered,
                isPractice: isPractice,
                accuracy: this.state.accuracy
            })
        }
        return response;
    }

    getPopUpInfo = () => {
        let accuracy = this.state.accuracy;
        let remainingQuestion = (this.state.isPractice && !this.state.isShowStartPopup && this.state.questionSkill?.questions?.length) ?
            this.state.questionSkill.questions.length - this.state.totalAttemptedQuestions : 0;
        let heading = ''
        let showAnimation = false;
        let isSafeZone = (!this.state.isShowAdaptivePopup) ? this.state.isSafeZone : false;
        switch (this.state.adaptivePracticeLevel) {
            case "Beginner":
                heading = "You are just getting started! Are you sure?"
                break;
            case "Developing":
                heading = "There are more questions to go! Are you sure?"
                break;
            case "Familiar":
                heading = "You are doing well! Are you sure?"
                break;
            case "Proficient":
                heading = "Just a few more questions to go! Are you sure?"
                break;
            case "Mastered":
                if (this.state.questionSkill.questions.length !== 0 && !this.state.isShowStartPopup)
                    showAnimation = true;
                heading = "Awesome! You have mastered this skill"
                break;
            default:
                heading = (this.state.isPractice && this.state.isShowStartPopup) ?
                    ((this.state.practiceCount < 2) ?
                        "You have already mastered this skill. You can get more practice with Math Buddy AI" :
                        "You’ve practiced this skill several times. For bigger XP boosts, explore other skills and keep progressing!") :
                    (remainingQuestion > 0) ? `${remainingQuestion} more questions to go. Are you sure` :
                        (accuracy === 0) ? 'You need to practice a lot' :
                            (accuracy < 50) ? 'Can do better with practice' :
                                (accuracy < 80) ? 'Keep practicing. You can do better' :
                                    (accuracy < 90) ? 'You did well. Try to score 100%' :
                                        'Way to go! You were perfect!';
                if (this.state.questionSkill?.questions?.length && !this.state.isShowStartPopup && remainingQuestion === 0)
                    showAnimation = true;
                break;
        }

        return {
            streaks: this.state.streaks,
            accuracy: accuracy,
            totalAttemptedQuestions: this.state.totalAttemptedQuestions,
            completedStatusPercentage: this.state.completedStatusPercentage,
            isShowStartPopup: this.state.isShowStartPopup,
            isSafeZone,
            isWarn: (this.state.isPractice && this.state.isShowStartPopup && this.state.practiceCount >= 2) ? true : false,
            isCloseFromClick: this.state.isCloseFromClick,
            heading, showAnimation
        }
    }

    setShowAdaptivePopup = (isCloseFromClick: Boolean = false) => {
        let isShowAdaptivePopup = !this.state.isShowAdaptivePopup
        this.setState({ isCloseFromClick, isShowAdaptivePopup });
    }

    setShowStartPopup = (status: boolean) => {
        this.setState({ isShowStartPopup: status })
        if (!status)
            this.loadSkillContent();
    }

    sendLastAttemptedIfAny = async () => {
        let lastAttemptedData = this.props.adaptivePracticeReducer.lastAttemptedQuestion;
        if (lastAttemptedData.questions[0].answers.length) {
            this.setState({ isLoader: true });
            await this.saveQuestionResponseToDB(lastAttemptedData);
            this.setState({ isLoader: false });
        }
    }

    addSmartXP = async () => {
        let smartXpSession = this.props.adaptivePracticeReducer.smartXpSession;
        if (smartXpSession?.user_id) {
            this.setState({ isLoader: true });
            this.context.messageForParent('addSmartXP', smartXpSession);
            this.setState({ isLoader: false });
            this.props.setAPSmartXpSession({ correct_count: 0 }); // reset correct count to 0;
        }
    }

    onClose = async () => {
        await this.sendLastAttemptedIfAny();
        await this.addSmartXP();
        this.context.messageForParent('close');
    }

    render() {
        return (
            <AdaptivePracticeContext.Provider value={{
                notificationMessage: this.state.notificationMessage,
                coins: this.state.coins,
                streaks: this.state.streaks,
                isSafeZone: this.state.isSafeZone,
                isLastQuestion: this.state.isLastQuestion,
                meter: {
                    percentage: this.state.completedStatusPercentage,
                    level: this.state.adaptivePracticeLevel
                },
                isLoader: this.state.isLoader,
                isCoinAnimation: this.state.isCoinAnimation,
                isShowStartPopup: this.state.isShowStartPopup,
                isShowAdaptivePopup: this.state.isShowAdaptivePopup,
                isCloseFromClick: this.state.isCloseFromClick,
                engineObj: this.state.response,
                loadContent: this.loadSkillContent,
                saveQuestionResponse: this.saveQuestionResponse,
                getPopUpInfo: this.getPopUpInfo,
                setShowAdaptivePopup: this.setShowAdaptivePopup,
                setShowStartPopup: this.setShowStartPopup,
                onclose: this.onClose
            }}>
                <PlayFrame></PlayFrame>
            </AdaptivePracticeContext.Provider>
        )
    }
}

function mapStateToProps(state: any) {
    return state;
}

let setAPLastAttempted = adaptivePracticeActions.setAPLastAttempted;
let setAPDefaultLastAttempted = adaptivePracticeActions.setAPDefaultLastAttempted;
let setAPSmartXpSession = adaptivePracticeActions.setAPSmartXpSession;


const mapDispatchToProps = { setAPLastAttempted, setAPDefaultLastAttempted, setAPSmartXpSession };

export default connect(mapStateToProps, mapDispatchToProps)(AdaptivePracticeForStudents);